import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { Card } from '../../components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LineChart, Line, Legend
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, addDays } from 'date-fns';

const SiteUsage = () => {
  const [logs, setLogs] = useState([]);
  const [eventType, setEventType] = useState('');
  const [courseId, setCourseId] = useState('');
  const [userId, setUserId] = useState('');
  const [groupBy, setGroupBy] = useState('event_type');
  const [allUsers, setAllUsers] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [courseMap, setCourseMap] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    const fetchFilters = async () => {
      const { data: logsData } = await supabase.from('site_usage_logs').select('event_details, user_id');
      const courseIds = [...new Set(logsData.map((d) => d.event_details?.course_id).filter(Boolean))];
      const userIds = [...new Set(logsData.map((d) => d.user_id))];
      setAllCourses(courseIds);
      setAllUsers(userIds);

      let users = [];
      if (userIds.length > 0) {
        const { data } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        users = data || [];
        console.log("👤 Users fetched:", users);
      }

      const userMapObj = {};
      users.forEach(u => {
        userMapObj[u.id] = u.full_name || u.email || u.id;
      });
      setUserMap(userMapObj);

      const { data: courses } = await supabase.from('courses').select('id, title');
      const courseMapObj = {};
      courses?.forEach(c => {
        courseMapObj[c.id] = c.title;
      });
      setCourseMap(courseMapObj);
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      let query = supabase
        .from('site_usage_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (eventType) query = query.eq('event_type', eventType);
      if (userId) query = query.eq('user_id', userId);
      if (courseId) query = query.contains('event_details', { course_id: courseId });
      if (startDate && endDate) {
        query = query
          .gte('created_at', format(startDate, "yyyy-MM-dd'T'00:00:00'Z'"))
          .lt('created_at', format(addDays(endDate, 1), "yyyy-MM-dd'T'00:00:00'Z'"));
      }

      const { data, error } = await query;
      if (!error) setLogs(data);
    };
    fetchLogs();
  }, [eventType, courseId, userId, startDate, endDate]);

  const getGroupKey = (log) => {
    if (groupBy === 'user_id') return userMap[log.user_id] || log.user_id;
    if (groupBy === 'nav_target') return log.event_details?.nav_target || 'Other';
    if (groupBy === 'course_id') return courseMap[log.event_details?.course_id] || log.event_details?.course_id || 'Unknown';
    return log.event_type;
  };

  const barChartData = Object.entries(
    logs.reduce((acc, log) => {
      const key = getGroupKey(log);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ).map(([label, count]) => ({ label, count }));

  const groupedLineData = logs.reduce((acc, log) => {
    const date = format(new Date(log.created_at), 'yyyy-MM-dd');
    const key = getGroupKey(log);
    if (!acc[date]) acc[date] = {};
    if (!acc[date][key]) acc[date][key] = 0;
    acc[date][key] += 1;
    return acc;
  }, {});

  const lineChartData = Object.entries(groupedLineData)
    .map(([date, groups]) => ({ date, ...groups }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const allGroupKeys = [...new Set(logs.map(getGroupKey))];

  return (
    <main className="p-4">
      <h1 className="text-2xl font-bold mb-4">📊 Analytics – Site Usage</h1>

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <select onChange={(e) => setEventType(e.target.value)} value={eventType} className="p-2 border rounded">
          <option value="">All Event Types</option>
          <option value="login">Login</option>
          <option value="course_view">Course View</option>
          <option value="lesson_start">Lesson Start</option>
          <option value="quiz_submit">Quiz Submit</option>
        </select>

        <select onChange={(e) => setCourseId(e.target.value)} value={courseId} className="p-2 border rounded">
          <option value="">Filter by Course (optional)</option>
          {allCourses.map((id) => (
            <option key={id} value={id}>{courseMap[id] || id}</option>
          ))}
        </select>

        <select onChange={(e) => setUserId(e.target.value)} value={userId} className="p-2 border rounded">
          <option value="">All Users</option>
          {allUsers.map((id) => (
            <option key={id} value={id}>{userMap[id] || id}</option>
          ))}
        </select>

        <select onChange={(e) => setGroupBy(e.target.value)} value={groupBy} className="p-2 border rounded">
          <option value="event_type">Group by Event Type</option>
          <option value="user_id">Group by User</option>
          <option value="nav_target">Group by Page</option>
          <option value="course_id">Group by Course</option>
        </select>

        <div className="flex items-center gap-2">
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            placeholderText="Start Date"
            maxDate={addDays(new Date(), 1)}
            className="p-2 border rounded"
          />
          <span>to</span>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            placeholderText="End Date"
            minDate={startDate}
            maxDate={addDays(new Date(), 1)}
            className="p-2 border rounded"
          />
        </div>
      </div>

      <div className="w-full h-[300px] mb-10">
        <h2 className="text-lg font-semibold mb-2">Event Frequency</h2>
        <ResponsiveContainer>
          <BarChart data={barChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" fill="#6366f1" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full h-[350px] mb-10">
        <h2 className="text-lg font-semibold mb-2">Activity Trends Over Time</h2>
        <ResponsiveContainer>
          <LineChart data={lineChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            {allGroupKeys.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key} strokeWidth={2} stroke={`hsl(${i * 70}, 70%, 50%)`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {logs.map((log) => (
          <Card key={log.id} className="p-4">
            <p><strong>Event:</strong> {log.event_type}</p>
            <p><strong>User:</strong> {userMap[log.user_id] || log.user_id}</p>
            <p><strong>Course:</strong> {courseMap[log.event_details?.course_id] || '—'}</p>
            <p><strong>Details:</strong> {JSON.stringify(log.event_details)}</p>
            <p className="text-xs text-gray-500">
              UTC: {log.created_at}<br />
              Local: {new Date(log.created_at).toLocaleString()}
            </p>
          </Card>
        ))}
      </div>
    </main>
  );
};

export default SiteUsage;
